import React, { useContext, useEffect, useState } from 'react';
import { Input, Label } from 'reactstrap';
import PropTypes from 'prop-types';
import { Trans } from '@lingui/react';
import { FocusedNodeContext } from '../../Service/Context/FocusedNode';
import { InputHelp } from './InputHelp';
import { useMantineTheme } from '@mantine/core';
import { extractTextFromHtml, removeInlineStylesFromHtml } from '../../Utils/htmlUtils';

export const SimpleEditableText = ({
  finishCallback,
  value: initialValue,
  placeholder,
  patchField,
  style,
  labelStyle,
  omitStylesOnPaste = false,
  ...rest
}) => {
  const { colorScheme, colors } = useMantineTheme();
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const { setListPosition } = useContext(FocusedNodeContext);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  function focusNode(e) {
    setListPosition(rest.index);
  }

  function handlePaste(e) {
    if (!omitStylesOnPaste) {
      return;
    }

    const html = e.clipboardData.getData('text/html');

    if (!html) {
      return;
    }

    e.preventDefault();

    const text = extractTextFromHtml(removeInlineStylesFromHtml(html));
    const input = e.currentTarget;
    const currentValue = value != null ? value : '';
    const selectionStart = input.selectionStart ?? currentValue.length;
    const selectionEnd = input.selectionEnd ?? currentValue.length;
    const nextValue = `${currentValue.slice(0, selectionStart)}${text}${currentValue.slice(selectionEnd)}`;
    const cursorPosition = selectionStart + text.length;

    setValue(nextValue);

    const restoreSelection = () => input.setSelectionRange(cursorPosition, cursorPosition);
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(restoreSelection);
    } else {
      restoreSelection();
    }
  }

  const styles = {
    error: {
      borderColor: 'red',
    },
    input: {
      marginBottom: 0,
      backgroundColor: colorScheme === 'dark' ? colors.dark[6] : 'white',
      color: colorScheme === 'dark' ? '#fff' : '#000',
    },
    label: {
      color: colorScheme === 'dark' ? '#fff' : '#000',
      ...labelStyle,
    },
    inputContainer: {
      marginBottom: 9,
      ...style,
    },
  };

  return (
    <div style={styles.inputContainer}>
      {placeholder && (
        <Label sm={12} style={styles.label}>
          <Trans id={placeholder} />
        </Label>
      )}
      <Input
        onBlur={() => {
          setLoading(true);
          setError(undefined);
          finishCallback({ [patchField]: value })
            .catch((error) => setError(error.message))
            .finally(() => {
              setLoading(false);
            });
        }}
        className={loading ? 'loading' : ''}
        style={error ? { ...styles.error, ...styles.input } : styles.input}
        onFocus={focusNode}
        onChange={(e) => setValue(e.target.value)}
        onPaste={handlePaste}
        onKeyUp={(e) => {
          switch (e.key) {
            case 'Enter':
              finishCallback({ [patchField]: value })
                .catch((error) => setError(error.message))
                .then(() => setLoading(false));
              break;
            case 'Escape':
              finishCallback({ [patchField]: initialValue })
                .catch((error) => setError(error.message))
                .then(() => setLoading(false));
          }
        }}
        value={value != null ? value : ''}
        {...rest}
      />
      {error ? <InputHelp errorDescription={error} /> : null}
    </div>
  );
};

SimpleEditableText.propTypes = {
  value: PropTypes.string,
  finishCallback: PropTypes.func,
  autoFocus: PropTypes.bool,
  omitStylesOnPaste: PropTypes.bool,
};

SimpleEditableText.defaultProps = {
  autoFocus: true,
  omitStylesOnPaste: false,
};
